import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useConnectWhatsApp,
  useCustomFields,
  useDisconnectWhatsApp,
  useLeadSources,
  useSaveWhatsAppSettings,
  useSendWhatsAppTest,
  useWhatsAppStatus,
  useWhatsAppTemplates,
} from '../api/hooks';
import type {
  WhatsAppMessageStatus,
  WhatsAppStatus,
  WhatsAppTemplate,
  WhatsAppVariable,
  WhatsAppVariableSource,
} from '../api/types';
import { EmptyState, ErrorBanner, formatDateTime, relativeTime } from '../components/ui';

/**
 * WhatsApp settings: connect a number, then choose the template every new lead
 * receives. Three cards in setup order, then the log of what was sent.
 */

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value} className="mono" onFocus={(e) => e.currentTarget.select()} />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

/* ------------------------------- connection ------------------------------- */

function ConnectionCard({ status }: { status: WhatsAppStatus }) {
  const connect = useConnectWhatsApp();
  const disconnect = useDisconnectWhatsApp();
  const [editing, setEditing] = useState(!status.connected);
  const [wabaId, setWabaId] = useState(status.connection?.waba_id ?? '');
  const [phoneNumberId, setPhoneNumberId] = useState(status.connection?.phone_number_id ?? '');
  const [token, setToken] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const result = await connect.mutateAsync({
        waba_id: wabaId.trim(),
        phone_number_id: phoneNumberId.trim(),
        access_token: token.trim(),
      });
      setToken('');
      setEditing(false);
      setNotice(
        result.subscribed
          ? 'Connected and verified with Meta.'
          : `Connected, but subscribing the app to delivery receipts failed: ${result.subscribe_error}. Messages will still send; sent/delivered/read will not update.`,
      );
    } catch (err) {
      setError(err);
    }
  }

  const c = status.connection;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>WhatsApp number</b>
          <p className="sub" style={{ margin: 0 }}>
            {status.connected && c
              ? `${c.display_phone ?? c.phone_number_id}${c.verified_name ? ` · ${c.verified_name}` : ''}`
              : 'Not connected'}
          </p>
        </div>
        {status.connected && !editing && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
              Change
            </button>
            <button
              className="btn btn-ghost btn-sm"
              disabled={disconnect.isPending}
              onClick={async () => {
                if (!window.confirm('Disconnect this number? Auto-messages stop immediately.')) return;
                setError(null);
                try {
                  await disconnect.mutateAsync();
                  setEditing(true);
                } catch (err) {
                  setError(err);
                }
              }}
            >
              Disconnect
            </button>
          </div>
        )}
      </div>

      <div className="card-body">
        <ErrorBanner error={error} />
        {notice && <div className="banner banner-info">{notice}</div>}

        {status.connected && c && !editing && (
          <dl className="kv">
            <dt>Phone number</dt>
            <dd>{c.display_phone ?? '—'}</dd>
            <dt>Display name</dt>
            <dd>{c.verified_name ?? '—'}</dd>
            <dt>Quality rating</dt>
            <dd>{c.quality_rating ?? '—'}</dd>
            <dt>Phone number ID</dt>
            <dd className="mono">{c.phone_number_id}</dd>
            <dt>WhatsApp Business Account ID</dt>
            <dd className="mono">{c.waba_id}</dd>
            <dt>Connected</dt>
            <dd>{formatDateTime(c.connected_at)}</dd>
          </dl>
        )}

        {editing && (
          <form onSubmit={submit}>
            <p className="hint" style={{ marginTop: 0 }}>
              In developers.facebook.com → your app → WhatsApp → API Setup you will find the{' '}
              <b>Phone number ID</b> and <b>WhatsApp Business Account ID</b>. The access token must be a
              permanent <b>System User</b> token (Business settings → Users → System users → Generate
              token) with <span className="mono">whatsapp_business_messaging</span> and{' '}
              <span className="mono">whatsapp_business_management</span> — the temporary token on the
              API Setup page expires within a day.
            </p>
            <div className="field">
              <label>
                WhatsApp Business Account ID <span className="req">*</span>
              </label>
              <input value={wabaId} onChange={(e) => setWabaId(e.target.value)} className="mono" required />
              {fieldErrors.waba_id && <div className="err">{fieldErrors.waba_id}</div>}
            </div>
            <div className="field">
              <label>
                Phone number ID <span className="req">*</span>
              </label>
              <input value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)} className="mono" required />
              {fieldErrors.phone_number_id && <div className="err">{fieldErrors.phone_number_id}</div>}
            </div>
            <div className="field">
              <label>
                System user access token <span className="req">*</span>
              </label>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="mono"
                autoComplete="off"
                placeholder={status.connected ? 'Required to change the connection' : 'EAA…'}
                required
              />
              {fieldErrors.access_token && <div className="err">{fieldErrors.access_token}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={connect.isPending}>
                {connect.isPending ? 'Verifying with Meta…' : 'Connect'}
              </button>
              {status.connected && (
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}

        <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--line, #e5e7eb)' }} />
        <p className="hint" style={{ marginTop: 0 }}>
          For delivered/read receipts, paste these into the Meta app under WhatsApp → Configuration →
          Webhook, then subscribe to the <span className="mono">messages</span> field.
        </p>
        {status.webhook.url ? (
          <CopyRow label="Callback URL" value={status.webhook.url} />
        ) : (
          <div className="banner banner-error">
            The server does not know its public URL. Set <span className="mono">PUBLIC_BASE_URL</span>.
          </div>
        )}
        {status.webhook.verify_token ? (
          <CopyRow label="Verify token" value={status.webhook.verify_token} />
        ) : (
          <p className="hint">Save a webhook verify token on the Meta Lead Ads page first — the same one is used here.</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ auto-message ------------------------------ */

const SOURCE_LABELS: Record<WhatsAppVariableSource, string> = {
  first_name: 'Lead first name',
  full_name: 'Lead full name',
  counsellor_name: 'Assigned team specialist first name',
  custom: 'Custom field',
  text: 'Fixed text',
};

function defaultBinding(param: string, index: number): WhatsAppVariable {
  return index === 0 ? { param, source: 'first_name', value: 'Parent' } : { param, source: 'text', value: '' };
}

function previewText(template: WhatsAppTemplate, vars: WhatsAppVariable[], fieldLabel: (key?: string) => string) {
  return template.body.replace(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g, (whole, name: string) => {
    const v = vars.find((x) => x.param === name);
    if (!v) return whole;
    switch (v.source) {
      case 'first_name':
        return 'Ravi';
      case 'full_name':
        return 'Ravi Kumar';
      case 'counsellor_name':
        return 'Priya';
      case 'custom':
        return `[${fieldLabel(v.field)}]`;
      case 'text':
        return v.value || '…';
    }
  });
}

function AutoMessageCard({ status }: { status: WhatsAppStatus }) {
  const templates = useWhatsAppTemplates(status.connected);
  const { data: fields = [] } = useCustomFields();
  const { data: sources = [] } = useLeadSources();
  const save = useSaveWhatsAppSettings();
  const test = useSendWhatsAppTest();

  const s = status.settings;
  const [enabled, setEnabled] = useState(s.auto_enabled);
  const [templateKey, setTemplateKey] = useState(
    s.template_name ? `${s.template_name}|${s.template_language}` : '',
  );
  const [vars, setVars] = useState<WhatsAppVariable[]>(s.variables);
  const [sourceIds, setSourceIds] = useState<string[]>(s.source_ids);
  const [quiet, setQuiet] = useState(Boolean(s.quiet_start));
  const [quietStart, setQuietStart] = useState(s.quiet_start ?? '21:00');
  const [quietEnd, setQuietEnd] = useState(s.quiet_end ?? '08:00');
  const [testTo, setTestTo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const template = useMemo(
    () => templates.data?.find((t) => `${t.name}|${t.language}` === templateKey),
    [templates.data, templateKey],
  );

  // Keep one binding per template variable when the template changes.
  useEffect(() => {
    if (!template) return;
    setVars((prev) => template.params.map((p, i) => prev.find((v) => v.param === p) ?? defaultBinding(p, i)));
  }, [template]);

  const fieldLabel = (key?: string) => fields.find((f) => f.api_key === key)?.label ?? key ?? 'field';
  const setVar = (param: string, patch: Partial<WhatsAppVariable>) =>
    setVars((prev) => prev.map((v) => (v.param === param ? { ...v, ...patch } : v)));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      const wasEnabled = s.auto_enabled;
      await save.mutateAsync({
        auto_enabled: enabled,
        template_name: template?.name ?? null,
        template_language: template?.language ?? null,
        variables: vars,
        source_ids: sourceIds,
        quiet_start: quiet ? quietStart : null,
        quiet_end: quiet ? quietEnd : null,
      });
      setNotice(
        enabled && !wasEnabled
          ? 'Saved. Every lead created from now on gets this message — existing leads are not messaged.'
          : 'Saved.',
      );
    } catch (err) {
      setError(err);
    }
  }

  async function sendTest() {
    if (!template) return;
    setError(null);
    setNotice(null);
    try {
      const result = await test.mutateAsync({
        to: testTo,
        template_name: template.name,
        template_language: template.language,
        variables: vars,
      });
      setNotice(`Test sent to ${testTo}: “${result.preview}”`);
    } catch (err) {
      setError(err);
    }
  }

  if (!status.connected) {
    return (
      <div className="card">
        <div className="card-head">
          <b>Auto-message new leads</b>
        </div>
        <div className="card-body">
          <p className="hint" style={{ margin: 0 }}>Connect a WhatsApp number first.</p>
        </div>
      </div>
    );
  }

  const usable = (templates.data ?? []).filter((t) => t.status === 'APPROVED');
  const others = (templates.data ?? []).filter((t) => t.status !== 'APPROVED');

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Auto-message new leads</b>
          <p className="sub" style={{ margin: 0 }}>
            {s.auto_enabled
              ? `On since ${formatDateTime(s.enabled_since)} — each new lead gets “${s.template_name}” once`
              : 'Off'}
          </p>
        </div>
      </div>
      <div className="card-body">
        <ErrorBanner error={error} />
        {notice && <div className="banner banner-info">{notice}</div>}

        <form onSubmit={submit}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, marginBottom: 16 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Send a WhatsApp message to every new lead
          </label>

          <div className="field">
            <label>
              Template <span className="req">*</span>
            </label>
            {templates.isPending ? (
              <span className="spinner" />
            ) : templates.error ? (
              <ErrorBanner error={templates.error} />
            ) : (
              <select value={templateKey} onChange={(e) => setTemplateKey(e.target.value)}>
                <option value="">— choose an approved template —</option>
                {usable.map((t) => (
                  <option
                    key={`${t.name}|${t.language}`}
                    value={`${t.name}|${t.language}`}
                    disabled={Boolean(t.unsupported_reason)}
                  >
                    {t.name} ({t.language}, {t.category.toLowerCase()})
                    {t.unsupported_reason ? ` — ${t.unsupported_reason}` : ''}
                  </option>
                ))}
              </select>
            )}
            <span className="hint">
              Templates are created and approved in WhatsApp Manager → Message templates.
              {others.length > 0 &&
                ` Not usable yet: ${others.map((t) => `${t.name} (${t.status.toLowerCase()})`).join(', ')}.`}
              {templates.data && templates.data.length === 0 && ' This account has no templates yet.'}
            </span>
          </div>

          {template && (
            <>
              {vars.map((v) => (
                <div className="field" key={v.param}>
                  <label>
                    Fill <span className="mono">{`{{${v.param}}}`}</span> with
                  </label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <select
                      value={v.source}
                      onChange={(e) => setVar(v.param, { source: e.target.value as WhatsAppVariableSource })}
                      style={{ flex: '1 1 200px' }}
                    >
                      {(Object.keys(SOURCE_LABELS) as WhatsAppVariableSource[]).map((k) => (
                        <option key={k} value={k}>
                          {SOURCE_LABELS[k]}
                        </option>
                      ))}
                    </select>
                    {v.source === 'custom' && (
                      <select
                        value={v.field ?? ''}
                        onChange={(e) => setVar(v.param, { field: e.target.value })}
                        style={{ flex: '1 1 200px' }}
                      >
                        <option value="">— field —</option>
                        {fields.filter((f) => f.is_active).map((f) => (
                          <option key={f.api_key} value={f.api_key}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    )}
                    <input
                      value={v.value ?? ''}
                      onChange={(e) => setVar(v.param, { value: e.target.value })}
                      placeholder={v.source === 'text' ? 'Text to send' : 'If the lead has no value, send…'}
                      title={v.source === 'text' ? undefined : 'Sent when the lead has no value for this'}
                      style={{ flex: '2 1 240px' }}
                    />
                  </div>
                </div>
              ))}

              <div className="field">
                <label>Preview</label>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    padding: 12,
                    borderRadius: 8,
                    background: 'var(--surface-2, rgba(37, 211, 102, 0.08))',
                    border: '1px solid var(--line, #e5e7eb)',
                  }}
                >
                  {previewText(template, vars, fieldLabel)}
                </div>
              </div>
            </>
          )}

          <div className="field">
            <label>Which leads</label>
            <span className="hint" style={{ marginTop: 0, marginBottom: 6 }}>
              None ticked = leads from every source.
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
              {sources.map((src) => (
                <label key={src.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={sourceIds.includes(src.id)}
                    onChange={(e) =>
                      setSourceIds((prev) => (e.target.checked ? [...prev, src.id] : prev.filter((x) => x !== src.id)))
                    }
                  />
                  {src.name}
                </label>
              ))}
            </div>
          </div>

          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={quiet} onChange={(e) => setQuiet(e.target.checked)} />
              Quiet hours
            </label>
            {quiet && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                Don’t send between
                <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} style={{ width: 130 }} />
                and
                <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} style={{ width: 130 }} />
                <span className="hint" style={{ margin: 0 }}>— leads arriving then are messaged when it ends.</span>
              </div>
            )}
          </div>

          <button className="btn btn-primary" disabled={save.isPending || (enabled && !template)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>

        {template && (
          <>
            <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--line, #e5e7eb)' }} />
            <div className="field">
              <label>Send a test to your own number</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="98765 43210"
                  style={{ flex: '1 1 200px' }}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={test.isPending || !testTo.trim()}
                  onClick={sendTest}
                >
                  {test.isPending ? 'Sending…' : 'Send test'}
                </button>
              </div>
              <span className="hint">Uses sample values (“Test”, “Counsellor”) — not saved against any lead.</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- log ----------------------------------- */

const STATUS_LABEL: Record<WhatsAppMessageStatus, string> = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
  failed: 'Failed',
  skipped: 'Skipped',
};

function MessagesCard({ status }: { status: WhatsAppStatus }) {
  const counts = status.counts_7d;
  const summary = (['sent', 'delivered', 'read', 'failed', 'skipped', 'queued'] as WhatsAppMessageStatus[])
    .filter((k) => counts[k])
    .map((k) => `${counts[k]} ${STATUS_LABEL[k].toLowerCase()}`)
    .join(' · ');

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Recent messages</b>
          <p className="sub" style={{ margin: 0 }}>{summary ? `Last 7 days: ${summary}` : 'Nothing sent yet'}</p>
        </div>
      </div>
      <div className="card-body">
        {status.recent.length === 0 ? (
          <EmptyState title="No messages yet">Messages appear here as new leads arrive.</EmptyState>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Lead</th>
                  <th>To</th>
                  <th>Status</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {status.recent.map((m) => (
                  <tr key={m.id}>
                    <td title={formatDateTime(m.created_at)}>{relativeTime(m.created_at)}</td>
                    <td>
                      <Link to={`/leads/${m.lead_id}`}>{m.lead_name ?? 'Lead'}</Link>
                    </td>
                    <td className="mono">{m.to_e164 ?? '—'}</td>
                    <td>
                      <span className={`chip${m.status === 'failed' ? '' : ' chip-plain'}`}>{STATUS_LABEL[m.status]}</span>
                    </td>
                    <td className="hint" style={{ maxWidth: 340 }}>
                      {m.error ?? (m.read_at ? `Read ${relativeTime(m.read_at)}` : m.delivered_at ? `Delivered ${relativeTime(m.delivered_at)}` : '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */

export function WhatsAppIntegrationPage() {
  const { data: status, isPending, error } = useWhatsAppStatus();

  if (isPending) {
    return (
      <div className="page">
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <span className="spinner" />
        </div>
      </div>
    );
  }
  if (!status) {
    return (
      <div className="page">
        <ErrorBanner error={error} />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>WhatsApp</h1>
          <p className="sub">Welcome every new lead on WhatsApp the moment they arrive, using an approved template</p>
        </div>
      </div>
      <ConnectionCard status={status} />
      {/* Keyed on the connection so connecting re-seeds the form from the server. */}
      <AutoMessageCard key={status.connection?.phone_number_id ?? 'none'} status={status} />
      <MessagesCard status={status} />
    </div>
  );
}
